import { featureProvider } from "@protontech/autofill/features/v1";
import type { DetectionClass, PerceptronParams } from "@protontech/autofill/types";
import { detectionClasses } from "@protontech/autofill/types";
import { FEATURE_SEPARATOR } from "@protontech/autofill/constants/features";
import type { Result } from "@protontech/autofill/types/utils";
import type { DecisionTree, RandomForest } from "@protontech/ml-inference";

const SUPPORTED_OBJECTIVES = new Set(["binary:logistic", "multi:softmax"]);

/** Every way a runtime-fetched weight artifact can fail validation against the current feature
 * store. Use `formatValidationProblem` to render one for a log line or telemetry event. */
export type ValidationProblem =
  | { kind: "missing-class"; klass: DetectionClass }
  | { kind: "extra-class"; klass: string }
  | { kind: "malformed-coeffs"; klass: DetectionClass }
  | { kind: "malformed-bias"; klass: DetectionClass }
  | { kind: "malformed-coeff"; klass: DetectionClass; index: number }
  | { kind: "unknown-features"; klass: DetectionClass; features: string[] }
  | { kind: "malformed-node-arrays"; klass: DetectionClass; treeIdx: number }
  | { kind: "mismatched-node-array-lengths"; klass: DetectionClass; treeIdx: number; arrays: string[] }
  | { kind: "out-of-range-split-index"; klass: DetectionClass; treeIdx: number; nodeIdx: number; featureIdx: number }
  | { kind: "out-of-range-child-index"; klass: DetectionClass; treeIdx: number; childIdx: number }
  | { kind: "malformed-forest-params"; klass: DetectionClass }
  | { kind: "unsupported-objective"; klass: DetectionClass; objective: string }
  | { kind: "empty-trees"; klass: DetectionClass }
  | { kind: "tree-info-length-mismatch"; klass: DetectionClass }
  | { kind: "out-of-range-tree-info-class-index"; klass: DetectionClass; treeIdx: number; classIdx: number };

/** Renders a `ValidationProblem` as a human-readable message, for local logging or telemetry. */
export const formatValidationProblem = (problem: ValidationProblem): string => {
  switch (problem.kind) {
    case "missing-class":
      return `missing weights for class ${problem.klass}`;
    case "extra-class":
      return `unexpected class in weight set: ${problem.klass}`;
    case "malformed-coeffs":
      return `${problem.klass}: missing or malformed coeffs`;
    case "malformed-bias":
      return `${problem.klass}: bias is not a finite number`;
    case "malformed-coeff":
      return `${problem.klass}: malformed coeff at index ${problem.index}`;
    case "unknown-features":
      return `${problem.klass}: unknown features not provided by the current feature store: [${problem.features.join(", ")}]`;
    case "malformed-node-arrays":
      return `${problem.klass}: tree ${problem.treeIdx} has a missing or malformed node array`;
    case "mismatched-node-array-lengths":
      return `${problem.klass}: tree ${problem.treeIdx} has mismatched node array lengths: [${problem.arrays.join(", ")}]`;
    case "out-of-range-split-index":
      return `${problem.klass}: tree ${problem.treeIdx} node ${problem.nodeIdx} has out-of-range split_indices ${problem.featureIdx}`;
    case "out-of-range-child-index":
      return `${problem.klass}: tree ${problem.treeIdx} has an out-of-range child index ${problem.childIdx}`;
    case "malformed-forest-params":
      return `${problem.klass}: missing or malformed feature_names/trees`;
    case "unsupported-objective":
      return `${problem.klass}: unsupported objective '${problem.objective}'`;
    case "empty-trees":
      return `${problem.klass}: trees is empty`;
    case "tree-info-length-mismatch":
      return `${problem.klass}: tree_info length does not match trees length`;
    case "out-of-range-tree-info-class-index":
      return `${problem.klass}: tree ${problem.treeIdx} has an out-of-range tree_info class index ${problem.classIdx}`;
    default: {
      const exhaustiveCheck: never = problem;
      return exhaustiveCheck;
    }
  }
};

/** The feature names the current feature store computes for `klass`, in the `<klass>--<feature>`
 * form weight files reference them by (see `rules/v1/index.ts`). A model is allowed to use only a
 * subset of these; a model referencing a name outside this set is what we reject. */
const expectedFeatureNames = (klass: DetectionClass): Set<string> => new Set(featureProvider[klass].features.map((name) => `${klass}${FEATURE_SEPARATOR}${name}`));

// Sorted so the `features`/`arrays` fields are deterministic regardless of the order names happen
// to appear in the artifact — callers formatting these into a message can rely on stable text.
const unknownNames = (names: string[], expected: Set<string>): string[] => names.filter((name) => !expected.has(name)).sort();

const toResult = <T>(problems: T[]): Result<void, T[]> => (problems.length > 0 ? { ok: false, error: problems } : { ok: true, value: undefined });

// `weights` is a runtime artifact cast to `Record<DetectionClass, T>`; that type doesn't
// guarantee the object actually has exactly those keys, so check explicitly. Per-class content
// validation only runs for classes actually present — a missing class is fully described by
// `missing-class` on its own, without a redundant "malformed" problem for its absent params.
const validateWeights = <T>(weights: Record<DetectionClass, T>, validateParamsForClass: (klass: DetectionClass, params: T) => ValidationProblem[]): Result<void, ValidationProblem[]> => {
  const found = new Set(Object.keys(weights));
  const missing: ValidationProblem[] = detectionClasses.filter((klass) => !found.has(klass)).map((klass) => ({ kind: "missing-class", klass }));
  const extra: ValidationProblem[] = [...found]
    .filter((klass) => !detectionClasses.includes(klass as DetectionClass))
    .sort()
    .map((klass) => ({ kind: "extra-class", klass }));
  const perClass = detectionClasses.filter((klass) => found.has(klass)).flatMap((klass) => validateParamsForClass(klass, weights[klass]));
  return toResult([...missing, ...extra, ...perClass]);
};

const validatePerceptronParamsForClass = (klass: DetectionClass, params: PerceptronParams): ValidationProblem[] => {
  if (!params || !Array.isArray(params.coeffs)) {
    return [{ kind: "malformed-coeffs", klass }];
  }

  const problems: ValidationProblem[] = [];

  if (!Number.isFinite(params.bias)) {
    problems.push({ kind: "malformed-bias", klass });
  }

  const names: string[] = [];
  params.coeffs.forEach((coeff, i) => {
    if (!Array.isArray(coeff) || typeof coeff[0] !== "string" || !Number.isFinite(coeff[1])) {
      problems.push({ kind: "malformed-coeff", klass, index: i });
      return;
    }
    names.push(coeff[0]);
  });

  const unknown = unknownNames(names, expectedFeatureNames(klass));
  if (unknown.length > 0) {
    problems.push({ kind: "unknown-features", klass, features: unknown });
  }

  return problems;
};

/** Validates a perceptron (logistic regression) weight set fetched at runtime against the
 * feature store this package currently ships. On failure, the error lists every problem found —
 * the artifact may be malformed or reference features the store doesn't provide. */
export const validatePerceptronWeights = (weights: Record<DetectionClass, PerceptronParams>): Result<void, ValidationProblem[]> => validateWeights(weights, validatePerceptronParamsForClass);

const validateTreeShape = (klass: DetectionClass, tree: DecisionTree, treeIdx: number, featureCount: number): ValidationProblem[] => {
  if (!tree || typeof tree !== "object") return [{ kind: "malformed-node-arrays", klass, treeIdx }];

  const problems: ValidationProblem[] = [];
  const nodeArrays: [string, unknown[]][] = [
    ["left_children", tree.left_children],
    ["right_children", tree.right_children],
    ["split_conditions", tree.split_conditions],
    ["split_indices", tree.split_indices],
    ["split_type", tree.split_type],
    ["default_left", tree.default_left],
  ];

  if (nodeArrays.some(([, arr]) => !Array.isArray(arr))) {
    problems.push({ kind: "malformed-node-arrays", klass, treeIdx });
    return problems;
  }

  const nodeCount = tree.left_children.length;
  const mismatched = nodeArrays
    .filter(([, arr]) => arr.length !== nodeCount)
    .map(([name]) => name)
    .sort();
  if (mismatched.length > 0) {
    problems.push({ kind: "mismatched-node-array-lengths", klass, treeIdx, arrays: mismatched });
    return problems;
  }

  tree.split_indices.forEach((featureIdx, nodeIdx) => {
    if (tree.left_children[nodeIdx] === -1) return; // leaf node: split_indices unused
    if (!Number.isInteger(featureIdx) || featureIdx < 0 || featureIdx >= featureCount) {
      problems.push({ kind: "out-of-range-split-index", klass, treeIdx, nodeIdx, featureIdx });
    }
  });

  [...tree.left_children, ...tree.right_children].forEach((childIdx) => {
    if (childIdx !== -1 && (!Number.isInteger(childIdx) || childIdx < 0 || childIdx >= nodeCount)) {
      problems.push({ kind: "out-of-range-child-index", klass, treeIdx, childIdx });
    }
  });

  return problems;
};

const validateRandomForestParamsForClass = (klass: DetectionClass, params: RandomForest): ValidationProblem[] => {
  if (!params || !Array.isArray(params.feature_names) || !Array.isArray(params.trees)) {
    return [{ kind: "malformed-forest-params", klass }];
  }

  const problems: ValidationProblem[] = [];

  const unknown = unknownNames(params.feature_names, expectedFeatureNames(klass));
  if (unknown.length > 0) {
    problems.push({ kind: "unknown-features", klass, features: unknown });
  }

  if (!SUPPORTED_OBJECTIVES.has(params.objective)) {
    problems.push({ kind: "unsupported-objective", klass, objective: params.objective });
  }

  if (params.trees.length === 0) {
    problems.push({ kind: "empty-trees", klass });
  } else {
    params.trees.forEach((tree, treeIdx) => problems.push(...validateTreeShape(klass, tree, treeIdx, params.feature_names.length)));
  }

  if (!Array.isArray(params.tree_info) || params.tree_info.length !== params.trees.length) {
    problems.push({ kind: "tree-info-length-mismatch", klass });
  } else if (params.objective === "multi:softmax") {
    params.tree_info.forEach((classIdx, treeIdx) => {
      if (!Number.isInteger(classIdx) || classIdx < 0 || classIdx >= params.num_class) {
        problems.push({ kind: "out-of-range-tree-info-class-index", klass, treeIdx, classIdx });
      }
    });
  }

  return problems;
};

/** Validates a random forest weight set fetched at runtime against the feature store this
 * package currently ships. On failure, the error lists every problem found — the artifact may be
 * malformed or reference features the store doesn't provide. */
export const validateRandomForestWeights = (weights: Record<DetectionClass, RandomForest>): Result<void, ValidationProblem[]> => validateWeights(weights, validateRandomForestParamsForClass);
